import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; 
import { 
    Plus, Search, Users, ChevronLeft, ChevronRight, 
    ChevronsLeft, ChevronsRight, ArrowUpDown, Filter, RefreshCw, Box, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { usePermissions } from './usePermissions';
// import { useFontSize } from './FontSizeContext'; // فعلها لو هتستخدمها

const PersonList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    const { canCreate } = usePermissions('persons'); // تأكد من اسم الموديول في الصلاحيات
    const [showAllSites, setShowAllSites] = useState(false);
    // const fontContext = useFontSize();
    const fontSize = /*fontContext?.fontSize ||*/ 12;

    const [persons, setPersons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const session = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionSite = session.site_id || '';
    const sessionOrg = session.org_id || '';
    
    const searchParams = new URLSearchParams(location.search);
    const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
    const [limit] = useState(50);
    const [sort, setSort] = useState({ 
        column: searchParams.get('sortBy') || 'personid', 
        direction: searchParams.get('sortDir') || 'asc' 
    });

    const [filters, setFilters] = useState({
        personid: searchParams.get('personid') || "",
        displayname: searchParams.get('displayname') || "",
        status: searchParams.get('status') || "",
        siteid: searchParams.get('siteid') || ""
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/person', {
                params: {
                    page, limit,
                    all_sites: Boolean(showAllSites), 
                    user_siteid: sessionSite, orgid: sessionOrg,
                    filter_siteid: filters.siteid || "", 
                    sortBy: sort.column, sortDir: sort.direction,
                    personid: filters.personid, displayname: filters.displayname, status: filters.status,
                },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setPersons(res.data.data);
            setTotal(res.data.total);
        } catch (err) {
            toast.error(t('persons.messages.loadFailed'));
        } finally { setLoading(false); }
    }, [page, showAllSites, filters, sort, sessionSite, sessionOrg, limit, t]);

    useEffect(() => {
        const timer = setTimeout(() => { fetchData(); }, 400); 
        return () => clearTimeout(timer);
    }, [fetchData]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(1); 
    };

    const handleRowClick = (personId) => {
        const queryParams = new URLSearchParams({
            page, ...filters, sortBy: sort.column, sortDir: sort.direction,
            all_sites: showAllSites, orgid: sessionOrg
        }).toString();
        navigate(`/persons/${personId}?${queryParams}`);
    };

    const handleSort = (column) => {
        setSort(prev => ({
            column, direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const clearFilters = () => {
        setFilters({ personid: "", displayname: "", status: "", siteid: "" });
        setPage(1);
    };

    useEffect(() => {
        const openAllSites = () => { setShowAllSites(true); setPage(1); };
        const viewMySite = () => { setShowAllSites(false); setPage(1); };
        window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
        window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        return () => {
            window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
            window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        };
    }, []);

    const totalPages = Math.ceil(total / limit) || 1;

    return (
        <div className={`flex flex-col h-full bg-[#f3f4f6] uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo dir-rtl text-right' : 'text-left font-black'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
            
            {/* Toolbar */}
            <div className="bg-white p-3 border-b shadow-sm z-20">
                <div className="flex justify-between items-center px-4">
                    <h2 className="text-[#1a3353] font-black italic uppercase text-lg flex items-center gap-2">
                        <Users size={22} className="text-blue-600" /> 
                        {t('persons.headers.directory')}
                        <RefreshCw size={18} className={`cursor-pointer transition-all ${loading ? 'animate-spin text-orange-500' : 'text-blue-400 hover:text-blue-600'} ${isRTL ? 'mr-2' : 'ml-2'}`} onClick={fetchData} />
                    </h2>
                    
                    <div className="flex gap-3">
                        <button onClick={clearFilters} className="flex items-center gap-2 px-4 py-2 text-gray-500 hover:text-red-600 font-bold transition-colors">
                            <X size={16}/> {t('po.actions.clearFilters')}
                        </button>
                        {/* {canCreate && ( */}
                            <button onClick={() => navigate('/persons/new')} className="bg-[#1a3353] text-white px-8 py-2 rounded-sm font-black flex items-center gap-2 hover:bg-blue-900 shadow-lg active:scale-95 transition-all">
                                <Plus size={16} /> {t('persons.actions.newPerson')}
                            </button>
                        {/* )} */}
                    </div>
                </div>
            </div>

            {/* Table Area */}
            <div className="flex-1 overflow-auto p-4 custom-scrollbar">
                <div className="bg-white border rounded-sm shadow-sm overflow-hidden">
                    <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                        <thead className="bg-[#1a3353] text-white font-bold uppercase sticky top-0 z-10">
                            <tr>
                                <th className="p-3 w-12 border-x border-white/10 text-center"></th>
                                <SortableHeader label={t('persons.tables.personId')} sortKey="personid" currentSort={sort} onSort={handleSort} isRTL={isRTL} />
                                <SortableHeader label={t('persons.tables.name')} sortKey="displayname" currentSort={sort} onSort={handleSort} isRTL={isRTL} />
                                <th className="p-3 border-x border-white/10 text-center w-28">{t('assets.headers.status')}</th>
                                <th className={`p-3 border-x border-white/10 text-center w-32`}>{t('persons.tables.site')}</th>
                            </tr>
                            <tr className="bg-[#f8fafc] shadow-inner">
                                <td className="p-1 border-x border-gray-200 bg-gray-100/50"></td>
                                <td className="p-1 border-x border-gray-200">
                                    <input className={`w-full p-1.5 text-blue-900 font-bold outline-none border border-gray-100 focus:border-blue-400 bg-white ${isRTL ? 'text-right' : ''}`} placeholder={t('common.filter')} value={filters.personid} onChange={e => handleFilterChange('personid', e.target.value)} />
                                </td>
                                <td className="p-1 border-x border-gray-200">
                                    <input className={`w-full p-1.5 text-blue-900 font-bold outline-none border border-gray-100 focus:border-blue-400 bg-white ${isRTL ? 'text-right' : ''}`} placeholder={t('common.filter')} value={filters.displayname} onChange={e => handleFilterChange('displayname', e.target.value)} />
                                </td>
                                <td className="p-1 border-x border-gray-200">
                                    <select className={`w-full p-1.5 text-blue-900 font-black outline-none border border-gray-100 bg-white text-center ${isRTL ? 'dir-rtl' : ''}`} value={filters.status} onChange={e => handleFilterChange('status', e.target.value)}>
                                        <option value="">{t('common.all')}</option>
                                        <option value="ACTIVE">ACTIVE</option>
                                        <option value="INACTIVE">INACTIVE</option>
                                    </select>
                                </td>
                                <td className="p-1 border-x border-gray-200">
                                    <input className="w-full p-1.5 text-blue-900 font-bold outline-none border border-gray-100 focus:border-blue-400 bg-white text-center" placeholder={t('common.filter')} value={filters.siteid} onChange={e => handleFilterChange('siteid', e.target.value)} />
                                </td>
                            </tr>
                        </thead>
                        
                        <tbody className="divide-y font-bold">
                            {loading ? (
                                <tr><td colSpan="5" className="p-20 text-center text-gray-300 font-black animate-pulse uppercase tracking-[0.2em]">{t('common.loading')}</td></tr>
                            ) : persons.length > 0 ? (
                                persons.map((p, i) => (
                                    <tr key={i} className="hover:bg-blue-50 cursor-pointer transition-colors group" onClick={() => handleRowClick(p.personid)}>
                                        <td className="p-2 border-x text-center">
                                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-black text-[14px] mx-auto group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                                {p.displayname?.[0] || '?'}
                                            </div>
                                        </td>
                                        <td className={`p-3 font-black text-blue-900 group-hover:underline ${isRTL ? 'border-l text-right' : 'border-r text-left'}`}>{p.personid}</td>
                                        <td className={`p-3 text-gray-600 font-bold italic ${isRTL ? 'border-l text-right' : 'border-r text-left'}`}>{p.displayname}</td>
                                        <td className={`p-3 border-x text-center`}>
                                            <span className={`px-3 py-1 rounded-full font-black text-[9px] shadow-sm border ${p.status === 'ACTIVE' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'}`}>
                                                {p.status}
                                            </span>
                                        </td>
                                        <td className={`p-3 text-center text-gray-500 font-bold border-x`}>{p.site_id || t('common.global')}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="5" className="p-20 text-center font-black text-gray-400 italic uppercase">{t('common.noData')}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Footer */}
            <div className="bg-[#1a3353] text-white p-2 px-8 flex justify-between items-center shadow-inner z-30 font-black uppercase tracking-widest text-[10px]">
                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                        <Box size={14} className="text-blue-400"/>
                        <span>{t('common.showing')}: <span className="text-blue-300 mx-1">{(page-1)*limit+1}-{Math.min(page*limit, total)}</span></span>
                    </div>
                    <div className={`flex items-center gap-2 border-white/20 px-6 ${isRTL ? 'border-r' : 'border-l'}`}>
                        <Filter size={14} className="text-orange-400"/>
                        <span>{t('common.total')}: <span className="text-orange-300 mx-1">{total}</span></span>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1 bg-black/20 rounded px-2 py-1 border border-white/10">
                        <button disabled={page === 1} onClick={() => setPage(1)} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all"><ChevronsLeft size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all"><ChevronLeft size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <div className="px-4 text-[11px] font-mono border-x border-white/10 mx-1 flex items-center gap-2">
                            <span className="text-orange-400 font-black text-sm">{page}</span>
                            <span className="text-gray-400 text-[9px]">{t('common.of')} {totalPages}</span>
                        </div>
                        <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all"><ChevronRight size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <button disabled={page >= totalPages} onClick={() => setPage(totalPages)} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all"><ChevronsRight size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Helpers ---
const SortableHeader = ({ label, sortKey, currentSort, onSort, align = "left", isRTL }) => {
    let justifyClass = 'justify-start';
    if (align === 'center') justifyClass = 'justify-center';
    if (align === 'right') justifyClass = isRTL ? 'justify-start' : 'justify-end';
    if (align === 'left' && isRTL) justifyClass = 'justify-end';

    return (
        <th className={`p-3 border-x border-white/10 cursor-pointer hover:bg-blue-800 transition-colors ${isRTL ? 'text-right' : 'text-left'}`} onClick={() => onSort(sortKey)}>
            <div className={`flex items-center gap-2 ${justifyClass}`}>
                {label}
                <ArrowUpDown size={12} className={currentSort.column === sortKey ? 'text-orange-400' : 'text-white/30'} />
            </div>
        </th>
    );
};

export default PersonList;